import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { Pill, type PillTone } from '../../components/Pill';
import { listBranches } from '../../data/fetchers';
import { fmtDate, fmtInt } from '../../lib/format';
import {
  getTransfer,
  receiveTransfer,
  type TransferView,
} from '../../lib/transfers';
import { useStockCatalog } from './stockCatalog';
import { StockHeader, type StockStatusTone } from './StockHeader';
import { TransferPrintModal } from './TransferPrintModal';

const STATUS_TH: Record<TransferView['status'], string> = {
  draft: 'ร่าง',
  sent: 'ส่งแล้ว',
  received: 'รับแล้ว',
  cancelled: 'ยกเลิก',
};
const STATUS_TONE: Record<TransferView['status'], PillTone> = {
  draft: 'neutral',
  sent: 'primary',
  received: 'ok',
  cancelled: 'danger',
};
const HEADER_STATUS_TONE: Record<TransferView['status'], StockStatusTone> = {
  draft: 'neutral',
  sent: 'warn',
  received: 'ok',
  cancelled: 'danger',
};
const fmtDateTime = (value: number): string =>
  new Intl.DateTimeFormat('th-TH-u-nu-latn', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

export function TransferDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [printOpen, setPrintOpen] = useState(false);
  const { data: transfer } = useQuery({
    queryKey: ['transfer', id],
    queryFn: () => getTransfer(id ?? ''),
    enabled: !!id,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const catalog = useStockCatalog(
    transfer?.lines.map((line) => line.productId) ?? [],
  );
  const products = catalog.products;
  const branchOf = (b: string) => branches.find((x) => x.id === b);
  const shortOf = (b: string) => branchOf(b)?.short ?? b;
  const branchNameOf = (b: string) => branchOf(b)?.name ?? b;
  const nameOf = (p: string) => products.find((x) => x.id === p)?.name ?? p;
  const receiveRequestId = useRef<string | null>(null);
  const receiveInFlight = useRef(false);

  const mutation = useMutation({
    mutationKey: ['transfer-receive'],
    meta: { errorRetry: true },
    mutationFn: (requestId: string) => receiveTransfer(id ?? '', requestId),
    onSuccess: () => {
      receiveRequestId.current = null;
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      void qc.invalidateQueries({ queryKey: ['movements'] });
      void qc.invalidateQueries({ queryKey: ['transfer', id] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onSettled: () => {
      receiveInFlight.current = false;
    },
  });
  const startReceive = () => {
    if (receiveInFlight.current) return;
    receiveInFlight.current = true;
    receiveRequestId.current ??= crypto.randomUUID();
    mutation.mutate(receiveRequestId.current);
  };

  if (!transfer)
    return (
      <div className="bo-fade">
        <StockHeader
          title="ใบโอน"
          subtitle="ไม่พบรายละเอียดของใบโอนนี้"
          status={{ label: 'ไม่พบข้อมูล', tone: 'danger' }}
        />
        <Card>ไม่พบใบโอน</Card>
      </div>
    );

  return (
    <div className="bo-fade mx-auto w-full max-w-5xl">
      <StockHeader
        title={`ใบโอน ${transfer.referenceNo}`}
        subtitle={`${shortOf(transfer.fromBranchId)} → ${shortOf(transfer.toBranchId)} · ${fmtDate(transfer.createdAt)}`}
        status={{
          label: STATUS_TH[transfer.status],
          tone: HEADER_STATUS_TONE[transfer.status],
        }}
        actions={
          <>
            <Btn
              disabled={catalog.loading || catalog.error}
              onClick={() => setPrintOpen(true)}
            >
              พิมพ์ใบโอน
            </Btn>
            {transfer.status === 'sent' && (
              <Btn
                kind="primary"
                onClick={startReceive}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'กำลังรับเข้า...' : 'รับเข้า'}
              </Btn>
            )}
          </>
        }
      />
      <Card title="สถานะ" className="mb-4">
        <Pill tone={STATUS_TONE[transfer.status]}>
          {STATUS_TH[transfer.status]}
        </Pill>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-4 text-body">
          <div>
            <dt className="text-ink-3">สร้างโดย</dt>
            <dd className="font-semibold text-ink-1">
              {transfer.createdByName ?? 'ไม่ทราบชื่อ'}
            </dd>
          </div>
          <div>
            <dt className="text-ink-3">สร้างเมื่อ</dt>
            <dd className="font-semibold text-ink-1">
              {fmtDateTime(transfer.createdAt)}
            </dd>
          </div>
          <div>
            <dt className="text-ink-3">รับโดย</dt>
            <dd className="font-semibold text-ink-1">
              {transfer.status === 'received'
                ? (transfer.receivedByName ?? 'ไม่ทราบชื่อ')
                : 'ยังไม่รับ'}
            </dd>
          </div>
          <div>
            <dt className="text-ink-3">รับเมื่อ</dt>
            <dd className="font-semibold text-ink-1">
              {transfer.receivedAt
                ? fmtDateTime(transfer.receivedAt)
                : 'ยังไม่รับ'}
            </dd>
          </div>
        </dl>
      </Card>
      <Card title="รายการสินค้า">
        <div className="overflow-hidden md:overflow-x-auto">
          <table className="bo-table bo-table-mobile-cards">
            <thead>
              <tr>
                <th>สินค้า</th>
                <th className="bo-th-num">จำนวน</th>
              </tr>
            </thead>
            <tbody>
              {transfer.lines.map((l, i) => (
                <tr key={i}>
                  <td data-label="สินค้า">{nameOf(l.productId)}</td>
                  <td data-label="จำนวน" className="bo-td-num num">
                    {fmtInt(l.qty)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="mt-4">
        <Btn kind="default" onClick={() => navigate('/transfers')}>
          กลับ
        </Btn>
      </div>
      <TransferPrintModal
        open={printOpen}
        transfer={transfer}
        fromBranchName={branchNameOf(transfer.fromBranchId)}
        toBranchName={branchNameOf(transfer.toBranchId)}
        products={products}
        onClose={() => setPrintOpen(false)}
      />
    </div>
  );
}
